import { useState, useEffect, useMemo, useId } from 'react'
import { Button, Modal, Select } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { useRoles } from '@/features/roles/hooks/useRoles'
import { useUser, useUpdateUserRole } from '../hooks/useUsers'

export interface ChangeUserRoleModalProps {
  userId: string | null
  onClose: () => void
  onSuccess: () => void
}

export function ChangeUserRoleModal({
  userId,
  onClose,
  onSuccess,
}: ChangeUserRoleModalProps): React.ReactElement {
  const formId = useId()
  const { data: user, isLoading: userLoading } = useUser(userId)
  const { data: roles = [] } = useRoles()
  const updateRole = useUpdateUserRole()
  const [selectedRoleId, setSelectedRoleId] = useState<string>(() => user?.roleId ?? '')

  const roleOptions: SelectOption[] = useMemo(
    () => roles.map((r) => ({ value: r._id, label: r.name })),
    [roles]
  )

  useEffect(() => {
    if (user?.roleId) setSelectedRoleId(user.roleId)
  }, [user?.roleId])

  const open = !!userId

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!userId || !selectedRoleId) return
    updateRole.mutate(
      { id: userId, payload: { roleId: selectedRoleId } },
      { onSuccess: () => onSuccess() }
    )
  }

  const apiError =
    updateRole.error instanceof Error
      ? updateRole.error.message
      : updateRole.error &&
          typeof updateRole.error === 'object' &&
          'response' in updateRole.error
        ? (updateRole.error as { response?: { data?: { message?: string } } }).response?.data?.message ?? null
        : null

  const showFooter = Boolean(user && !userLoading)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change user role"
      className="max-w-sm"
      footer={
        showFooter ? (
          <>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              form={formId}
              type="submit"
              disabled={roleOptions.length === 0} loading={updateRole.isPending}
            >
              {updateRole.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        ) : null
      }
    >
      {!userId ? null : userLoading ? (
        <SectionLoader variant="detail" rows={2} label="Loading user" />
      ) : !user ? null : (
        <form id={formId} onSubmit={handleSubmit} className="space-y-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Update role for{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {user.firstName} {user.lastName}
            </span>
            .
          </p>
          {roleOptions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No roles available. Create a role first.
            </p>
          ) : (
            <Select
              label="Role"
              options={roleOptions}
              value={selectedRoleId}
              onChange={setSelectedRoleId}
            />
          )}
          {apiError && (
            <p className="text-sm text-danger" role="alert">
              {apiError}
            </p>
          )}
        </form>
      )}
    </Modal>
  )
}

ChangeUserRoleModal.displayName = 'ChangeUserRoleModal'
